<template>
  <md-list-item class="case-card" :class="{ selected: selected }" @click="load">
    <div class="container">
      <div class="info-container">
        <div v-if="showUpdateColumn" class="case-update column">
          <help-tooltip v-if="caseConf.needsEcUpdate && (!updateStatus || updateStatus === 'YES' || updateStatus === 'NO')">
            <template slot="trigger">
              <radio-buttons
                :options="ecUpdateOptions"
                :value="state.willEcUpdate"
                bordered
                compact
                @input="willEcUpdate => setWillEcUpdate(willEcUpdate)"
              />
            </template>
            <div class="case-updates-tooltip">
              <h2 class="title">Update This Case? 🔁</h2>
              <h3 class="subtitle">Case updates include</h3>
              <span v-for="outdatedMedication in caseConf.outdatedMedications" :key="outdatedMedication.id" class="update-item">
                Medication: {{ outdatedMedication.name.en }}
              </span>
              <span v-for="outdatedEcNode in caseConf.outdatedEcNodes" :key="outdatedEcNode.id" class="update-item">
                Case logic node {{ outdatedEcNode.id }}: {{ outdatedEcNode.baseEffectChain.name.en }}
              </span>
            </div>
          </help-tooltip>
          <span v-else-if="updateStatus === 'UPDATED'" class="updated">Updated</span>
          <span v-else-if="caseConf.needsEcUpdate || updateStatus === 'ERRORRED'" class="errored">Error</span>
          <span v-else-if="caseConf.ignoreEcUpdate || updateStatus === 'DECLINED'" class="declined">Declined</span>
          <span v-else class="no-action">No Action</span>
        </div>
        <div class="case-name column">
          <span>{{ caseConf.name }}</span>
          <help-tooltip v-if="caseConf.errorsCount" type="error">Case information needs updated</help-tooltip>
        </div>
        <div v-for="column in displayedColumns" :key="column.field" :class="`column ${column.field}`" :style="{flex: column.size}">
          <div v-if="column.field === '_avatar'" class="avatar-container">
            <img :src="pictureFor(avatar)">
            <span>{{ nameFor(avatar) }}</span>
          </div>
          <span v-if="column.field === '_startLocation'">{{ nameFor(caseConf._startLocation) }}</span>
          <span v-if="column.field === '_endLocation'">{{ nameFor(caseConf._endLocation) }}</span>
          <span v-if="column.field === 'origin'">{{ origin() }}</span>
          <span v-if="column.field === 'access.owner'">{{ owner() }}</span>
          <span v-if="column.field === 'lastPlayed'">{{ date(caseConf.lastPlayed) }}</span>
          <span v-if="column.field === 'lastAccessed'">{{ date(caseConf.lastAccessed) }}</span>
          <span v-if="column.field === 'createdAt'">{{ date(caseConf.createdAt) }}</span>
          <md-checkbox v-if="column.field === 'published'" :value="isPublished(caseConf)" :class="column.class" />
          <md-checkbox v-if="column.field === 'applab'" :value="hasAppLabAccess" :class="column.class" />
        </div>
      </div>
      <md-button v-if="showFavorites" class="md-icon-button favorite-button" @click="toggleFavorite">
        <md-icon class="favorite-icon" :class="{ active: caseConf.isFavorite }" label="Favorite">star</md-icon>
      </md-button>
      <div v-if="showSpacer" class="columns-edit-spacer" />
    </div>
  </md-list-item>
</template>

<script>
import get from 'lodash/get';
import moment from 'moment';

import { isPublished, hasAppLabAccess } from '~lib/common/utils/publish';
import RadioButtons from '~lib/common/components/RadioButtons';
import HelpTooltip from '~lib/common/components/HelpTooltip';

import questionMarkPicture from '../../assets/images/question-mark.png';

export default {
  name: 'case-card',
  components: {
    RadioButtons,
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    origin: {
      type: Function,
      default: () => undefined,
    },
    owner: {
      type: Function,
      default: () => undefined,
    },
    removable: {
      type: Boolean,
    },
    showFavorites: {
      type: Boolean,
      default: true,
    },
    showSpacer: {
      type: Boolean,
    },
    selected: {
      type: Boolean,
    },
    displayedColumns: {
      type: Array,
      default: () => [],
    },
    showUpdateColumn: {
      type: Boolean,
      default: false,
    },
    updateStatus: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      state: {
        willEcUpdate: null,
      },
      ecUpdateOptions: [
        {
          value: true,
          label: 'Y',
          activeColor: 'green-3',
          inactiveColor: 'white',
          tooltip: 'Update this case',
        },
        {
          value: false,
          label: 'N',
          activeColor: 'red-3',
          inactiveColor: 'white',
          tooltip: 'Don\'t update this case',
        },
      ]
    }
  },
  computed: {
    avatar() {
      return (
        this.caseConf._avatar || {
          name: {
            en: 'No avatar',
            es: 'No avatar',
          },
          picture: {
            url: questionMarkPicture,
          },
        }
      );
    },
    hasAppLabAccess() {
      return hasAppLabAccess(this.caseConf, this.caseConf.access.productType);
    }
  },
  methods: {
    load() {
      this.$emit('load', this.caseConf.id);
    },
    remove() {
      this.$emit('remove', this.caseConf.id);
    },
    toggleFavorite() {
      this.$emit('favorite', this.caseConf);
    },
    pictureFor(item) {
      return get(item, 'picture.url', questionMarkPicture);
    },
    nameFor(item) {
      return get(item, 'name.en', '--');
    },
    date(date) {
      return date ? moment(date).format('YYYY-MM-DD') : '';
    },
    setWillEcUpdate(value) {
      this.state.willEcUpdate = value;
      this.$emit('updateStatusChange', this.caseConf, value ? 'YES' : 'NO');
    },
    isPublished,
  },
};
</script>

<style scoped>
.case-card {
  height: 66px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.5);
  font-size: 18px;
  font-weight: normal;
  text-align: left;
  list-style: none;
}
.case-card.selected {
  background-color: rgba(37, 149, 211, 0.21);
}

.container {
  display: flex;
  align-items: center;
  width: 100%;
  flex-direction: row;
}
span {
  color: black;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info-container {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  min-width: 0;
  flex: 1 1 auto;
}
.avatar-container {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  flex-direction: column;
  width: 80px;
  height: 66px;
  font-size: 14px;
}
.avatar-container img {
  border-radius: 50%;
  height: 40px;
  width: 40px;
  box-shadow: 1px 1px 4px 0 rgba(0,0,0,.4);
}

.case-update {
  align-self: stretch;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 100px;
  box-shadow: 0 3px 24px 0 rgba(0, 0, 0, 0.26);
}

.case-update .no-action,
.case-update .updated,
.case-update .declined {
  color: #d2d2d2;
  text-transform: uppercase;
}

.case-update .updated,
.case-update .declined {
  border: solid 1px #d2d2d2;
  border-radius: 6px;
  padding: 5px;
  text-transform: uppercase;
}

.case-updates-tooltip {
  color: white;
  text-align: left;
  padding: 20px;
}

.case-updates-tooltip .title {
  font-size: 24px;
  margin-top: 0px;
  margin-bottom: 5px;
}

.case-updates-tooltip .subtitle {
  color: #bbbbbb;
  font-size: 18px;
  margin-top: 5px;
  margin-bottom: 10px;
}

.case-updates-tooltip .update-item {
  color: #bbbbbb;
  font-size: 18px;
}

.case-update .errored {
  color: #ff5252;
  text-transform: uppercase;
  border: solid 1px #ff5252;
  border-radius: 6px;
  padding: 5px;
}

.case-name {
  margin-left: 20px;
  flex: 2;
  display: flex;
  flex-direction: row;
  align-items: center;
}

.case-name .help-tooltip {
  margin-left: 8px;
}

.favorite-button {
  width: 40px;
  border-radius: 50%;
  margin: 0 10px 0 0;
}
.favorite-icon {
  color: #5F5F5F !important;
  font-size: 24px !important;
}
.favorite-icon.active {
  color: #FFB800 !important;
}

.column {
  flex-basis: 0;
  width: 0;
  text-overflow: ellipsis;
  overflow: hidden;
}

.column.isFavorite {
  text-align: center;
}

.columns-edit-spacer {
  /* 8px = size of scroll bar */
  width: calc(40px - 8px);
}

.md-checkbox.md-checked.applab >>> .md-checkbox-container {
  background-color: #009688;
  border-color: #009688;
}

</style>

<style>
.case-card .md-list-item-content {
  padding: 0;
}
</style>
